/**
 * Hardware Configuration Service
 * Manages NFC tags, QR codes, BLE beacons, and device management (MDM/BYOD)
 * for the mobile point-of-care app.
 */

import LocalStorageService from '../storage/LocalStorageService';

class HardwareConfigService {
  constructor() {
    this.storagePrefix = 'hardware_config';
  }

  // ─── NFC Tags ───────────────────────────────────────────────────────

  getCompatibleNFCModels() {
    return [
      { model: 'NTAG 213', memory: '144 bytes', range: '3-5 cm', recommended: true, priceAUD: 0.85, notes: 'Best for room tagging — small, durable, waterproof sticker variants available.' },
      { model: 'NTAG 215', memory: '504 bytes', range: '3-5 cm', recommended: false, priceAUD: 1.20, notes: 'Larger memory for extended payloads. Same form factor.' },
      { model: 'NTAG 216', memory: '888 bytes', range: '3-5 cm', recommended: false, priceAUD: 1.50, notes: 'Maximum storage. Overkill for room-ID use case.' },
      { model: 'Mifare Classic 1K', memory: '1 KB', range: '3-10 cm', recommended: false, priceAUD: 0.90, notes: 'Widely available but crypto vulnerabilities. Not recommended for new deployments.' },
    ];
  }

  async programNFCTag(tagId, roomId, residentId) {
    try {
      const record = {
        id: tagId || `NFC-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        type: 'nfc',
        roomId,
        residentId,
        payload: JSON.stringify({ type: 'medfast_room', roomId, residentId, version: 1 }),
        programmedAt: new Date().toISOString(),
        status: 'active',
        lastScanned: null,
        scanCount: 0,
      };

      const tags = LocalStorageService.get(`${this.storagePrefix}_nfc_tags`, []);
      tags.push(record);
      LocalStorageService.set(`${this.storagePrefix}_nfc_tags`, tags);

      console.info('[HardwareConfig] NFC tag programmed', { tagId: record.id, roomId });
      return { success: true, data: record };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  getNFCDeploymentGuide() {
    return {
      placement: [
        'Mount tag on door frame at 120 cm height (wheelchair accessible from inside)',
        'Use epoxy-backed tags for permanent mounting or adhesive stickers for temporary',
        'Place a secondary tag inside the room near the bedside for bedside charting scan-in',
        'Ensure tag is not obscured by door handles, signage, or decoration',
      ],
      programming: [
        'Use MedFast NFC Writer app (or NFC Tools Pro) to write NDEF record',
        'Payload format: JSON with type=medfast_room, roomId, residentId, version',
        'Lock tag after programming to prevent accidental overwrite',
        'Record serial number in asset register for audit trail',
      ],
      maintenance: [
        'NFC tags are passive — no battery required',
        'Replace if physically damaged or if read failures exceed 5% in weekly scan audit',
        'Re-program on resident room change (update residentId in payload)',
      ],
    };
  }

  // ─── QR Codes ───────────────────────────────────────────────────────

  async generateQRCode(roomId, residentId) {
    try {
      const payload = {
        type: 'medfast_room',
        roomId,
        residentId,
        facilityId: 'current',
        generated: new Date().toISOString(),
        version: 1,
      };

      const record = {
        id: `QR-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        type: 'qr',
        roomId,
        residentId,
        payload: JSON.stringify(payload),
        dataUrl: `data:image/svg+xml;base64,${btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="#fff" width="200" height="200"/><text x="50%" y="50%" text-anchor="middle" dy=".35em" font-size="14">${roomId}</text></svg>`)}`,
        generatedAt: new Date().toISOString(),
        printedAt: null,
        status: 'generated',
      };

      const codes = LocalStorageService.get(`${this.storagePrefix}_qr_codes`, []);
      codes.push(record);
      LocalStorageService.set(`${this.storagePrefix}_qr_codes`, codes);

      console.info('[HardwareConfig] QR code generated', { id: record.id, roomId });
      return { success: true, data: record };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  getQRPlacementGuide() {
    return {
      printing: [
        'Print on water-resistant label stock (minimum 80mm x 80mm)',
        'Use UV-resistant laminate for outdoor or sun-exposed locations',
        'Include human-readable room number below QR code',
        'Print batch with facility branding for professional appearance',
      ],
      placement: [
        'Mount at 120 cm height on door frame (same as NFC)',
        'Backup QR inside room near bedside table',
        'Ensure adequate lighting for camera scanning',
        'Avoid reflective surfaces behind QR code',
      ],
    };
  }

  // ─── BLE Beacons ────────────────────────────────────────────────────

  getCompatibleBLEModels() {
    return [
      { model: 'Kontakt.io Smart Beacon SB18', battery: 'CR2477 (2-4 years)', range: '1-70 m (configurable)', priceAUD: 35.00, recommended: true, notes: 'Industry leader. Cloud management. Good for room-level tracking.' },
      { model: 'Estimote Proximity Beacon', battery: 'Built-in rechargeable (3 years)', range: '0.5-50 m', priceAUD: 42.00, recommended: false, notes: 'Mesh networking capable. Premium build quality.' },
      { model: 'Minew E8', battery: 'CR2477 (3-5 years)', range: '1-80 m', priceAUD: 18.00, recommended: false, notes: 'Budget option. iBeacon + Eddystone dual support. Good for high-volume deployment.' },
      { model: 'Teltonika EYE Beacon', battery: 'CR2450 (2-3 years)', range: '1-100 m', priceAUD: 28.00, recommended: false, notes: 'Industrial grade. IP67 rated.' },
    ];
  }

  async registerBLEBeacon(beaconConfig) {
    try {
      const record = {
        id: beaconConfig.beaconId || `BLE-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        model: beaconConfig.model,
        uuid: beaconConfig.uuid || this._generateUUID(),
        major: beaconConfig.major || 1,
        minor: beaconConfig.minor || Math.floor(Math.random() * 65535),
        roomId: beaconConfig.roomId,
        facilityId: beaconConfig.facilityId,
        txPower: beaconConfig.txPower || -12, // dBm
        advertisingInterval: beaconConfig.advertisingInterval || 350, // ms
        batteryLevel: 100,
        batteryInstalledAt: new Date().toISOString(),
        estimatedBatteryExpiry: new Date(Date.now() + 3 * 365 * 24 * 60 * 60 * 1000).toISOString(),
        status: 'active',
        registeredAt: new Date().toISOString(),
        lastSeen: null,
      };

      const beacons = LocalStorageService.get(`${this.storagePrefix}_ble_beacons`, []);
      beacons.push(record);
      LocalStorageService.set(`${this.storagePrefix}_ble_beacons`, beacons);

      console.info('[HardwareConfig] BLE beacon registered', { id: record.id, roomId: record.roomId });
      return { success: true, data: record };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async checkBeaconBatteryLevels(facilityId) {
    try {
      const beacons = LocalStorageService.get(`${this.storagePrefix}_ble_beacons`, []);
      const facilityBeacons = beacons.filter(b => b.facilityId === facilityId);

      const now = new Date();
      const alerts = [];
      for (const beacon of facilityBeacons) {
        const expiry = new Date(beacon.estimatedBatteryExpiry);
        const daysUntilExpiry = Math.floor((expiry - now) / (1000 * 60 * 60 * 24));

        if (daysUntilExpiry <= 30) {
          alerts.push({
            beaconId: beacon.id,
            roomId: beacon.roomId,
            severity: daysUntilExpiry <= 7 ? 'critical' : 'warning',
            daysRemaining: daysUntilExpiry,
            message: `Beacon ${beacon.id} in room ${beacon.roomId}: battery replacement needed within ${daysUntilExpiry} days`,
          });
        }
      }

      return { success: true, totalBeacons: facilityBeacons.length, alerts };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  getBLERangeConfiguration() {
    return {
      roomLevel: { txPower: -12, advertisingInterval: 350, expectedRange: '3-5 m', useCase: 'Precise room presence detection' },
      wingLevel: { txPower: -4, advertisingInterval: 500, expectedRange: '10-15 m', useCase: 'Wing/corridor proximity' },
      facilityLevel: { txPower: 0, advertisingInterval: 700, expectedRange: '30-50 m', useCase: 'Building-wide asset tracking' },
    };
  }

  // ─── Device Management ──────────────────────────────────────────────

  async registerDevice(device) {
    try {
      const record = {
        id: device.deviceId || `DEV-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        type: device.type, // 'facility_owned' | 'byod'
        model: device.model,
        os: device.os,
        osVersion: device.osVersion,
        assignedTo: device.assignedTo,
        facilityId: device.facilityId,
        mdmEnrolled: device.type === 'facility_owned',
        mdmProfile: device.type === 'facility_owned' ? this._getMDMProfile() : null,
        byodCompliant: device.type === 'byod' ? this._checkBYODCompliance(device) : null,
        status: 'active',
        registeredAt: new Date().toISOString(),
        lastCheckIn: new Date().toISOString(),
      };

      const devices = LocalStorageService.get(`${this.storagePrefix}_devices`, []);
      devices.push(record);
      LocalStorageService.set(`${this.storagePrefix}_devices`, devices);

      console.info('[HardwareConfig] Device registered', { id: record.id, type: record.type });
      return { success: true, data: record };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  _getMDMProfile() {
    return {
      provider: 'Microsoft Intune',
      policies: [
        'Device encryption required',
        'Screen lock timeout: 2 minutes',
        'Minimum OS version enforced',
        'App whitelist applied',
        'Camera restricted in clinical areas',
        'USB debugging disabled',
        'Remote wipe enabled',
        'Automatic updates enforced',
      ],
      complianceChecks: ['encryption', 'passcode', 'os_version', 'jailbreak_detection', 'app_inventory'],
    };
  }

  _checkBYODCompliance(device) {
    const requirements = {
      minOSVersions: { iOS: '16.0', Android: '13' },
      encryptionRequired: true,
      passcodeRequired: true,
      biometricRecommended: true,
    };

    return {
      meetsRequirements: true,
      requirements,
      notes: 'BYOD devices must have MedFast app containerised (work profile on Android, managed app on iOS). Personal data is separated from clinical data.',
    };
  }

  getBYODPolicy() {
    return {
      title: 'MedFast BYOD Policy for Aged Care Staff',
      requirements: [
        'Device must run iOS 16+ or Android 13+ with latest security patches',
        'Device encryption must be enabled',
        'Screen lock with PIN/biometric required (max 2-minute timeout)',
        'MedFast app runs in managed container — no clinical data stored outside container',
        'Staff must consent to remote wipe of MedFast container (not personal data) if device is lost',
        'Jailbroken or rooted devices are prohibited',
        'Staff responsible for their own device hardware, insurance, and repairs',
        'MedFast provides the app licence and data plan reimbursement ($30/month)',
      ],
      dataHandling: [
        'Clinical data is encrypted at rest within the app container (AES-256)',
        'Offline cache is cleared after 24 hours of no connectivity',
        'No screenshots or screen recordings permitted within MedFast app',
        'Clipboard sharing between MedFast and personal apps is disabled',
      ],
    };
  }

  // ─── Internal ───────────────────────────────────────────────────────

  _generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }
}

export default new HardwareConfigService();
